import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { Grid, Paper } from "@mui/material";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { useAuthUser } from "hooks/authentication";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type FC, useContext } from "react";
import { checkInternalUser } from "service/utils/get-user-type";
import { NoSearchResults } from "../NoSearchResults";
import { SearchResultsGridLoadingTile } from "./SearchResultsGridLoadingTile";
import { SearchResultsGridTile } from "./SearchResultsGridTile";

export interface SearchResultsGridProps {
  values?: Array<ColoredMaterial>;
  target?: NamedColor;
  onFilterReset?: () => void;
  loading?: boolean;
  gridSize?: number;
  onMaterialSelect?: (material: ColoredMaterial) => void;
}

export const SearchResultsGrid: FC<SearchResultsGridProps> = ({
  values,
  target,
  onFilterReset,
  loading,
  gridSize = 2,
  onMaterialSelect,
}) => {
  const imageAspectRatio = "1 / 1";
  const imageType = gridSize > 2 ? "THUMBNAIL" : "SQUARE";

  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const { user } = useAuthUser();
  const isInternal: boolean = checkInternalUser(user, settings);

  return (
    <Grid
      sx={{ width: "100%" }}
      container
      spacing={2}
      columns={{
        xxl: gridSize + 4,
        xl: gridSize + 3,
        lg: gridSize + 2,
        md: gridSize + 2,
        sm: gridSize + 1,
        xs: gridSize,
      }}
    >
      {loading &&
        Array.from(Array(14)).map((_, i) => (
          <Grid key={i} size={1}>
            <SearchResultsGridLoadingTile imageAspectRatio={imageAspectRatio} />
          </Grid>
        ))}
      {!loading &&
        values?.map((value) => (
          <Grid key={value.eljExperimentId + value.eljExperimentBatch} size={1}>
            <SearchResultsGridTile
              material={value}
              target={target}
              imageAspectRatio={imageAspectRatio}
              imageType={imageType}
              isInternal={isInternal}
              onMaterialSelect={onMaterialSelect}
            />
          </Grid>
        ))}
      {!loading && values?.length === 0 && (
        <Grid size={12} fontSize="var(--body-b2-14-regular-size, 14px)">
          <Paper sx={{ padding: 2 }}>
            <NoSearchResults onFilterReset={onFilterReset} />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};
